// components/AsthmaPopups.tsx
import React from 'react';
import { GenericPopup } from './GenericPopup';
import { EmailSummaryPopup, DepartmentMember } from './EmailSummaryPopup';
import { MildSectionKey } from '../../utils/asthmaTypes';

interface AsthmaPopupsProps {
  // === State Variables ===
  showEmailPopup: boolean;
  showIncompletePopup: boolean;
  showAllergyPopup: boolean;
  showSteroidPopup: boolean;
  showCloseConfirmation: boolean;
  showPrematureClosePopup: boolean;
  showLoginRequiredPopup: boolean;
  isEmailSent: boolean;
  pendingRecheckTarget: MildSectionKey | null;
  defaultEmail: string;
  departmentMembers?: DepartmentMember[];

  // === State Setters ===
  setShowEmailPopup: (show: boolean) => void;
  setShowIncompletePopup: (show: boolean) => void;
  setShowAllergyPopup: (show: boolean) => void;
  setShowSteroidPopup: (show: boolean) => void;
  setShowCloseConfirmation: (show: boolean) => void;
  setShowPrematureClosePopup: (show: boolean) => void;
  setShowLoginRequiredPopup: (show: boolean) => void;

  // === Actions ===
  onOpenSection: (section: MildSectionKey) => void;
  onSendEmail: (email: string | null, patientName: string) => void;
  onRequestEmail: () => void;
  onReset: () => void;
  onSkipToRecheck: (round: number) => void;
}

export const AsthmaPopups: React.FC<AsthmaPopupsProps> = ({
  // State
  showEmailPopup,
  showIncompletePopup,
  showAllergyPopup,
  showSteroidPopup,
  showCloseConfirmation,
  showPrematureClosePopup,
  showLoginRequiredPopup,
  isEmailSent,
  pendingRecheckTarget,
  defaultEmail,
  departmentMembers = [],

  // Setters
  setShowEmailPopup,
  setShowIncompletePopup,
  setShowAllergyPopup,
  setShowSteroidPopup,
  setShowCloseConfirmation,
  setShowPrematureClosePopup,
  setShowLoginRequiredPopup,

  // Actions
  onOpenSection,
  onSendEmail,
  onRequestEmail,
  onReset,
  onSkipToRecheck,
}) => {
  return (
    <>
      {/* 1. Popup for Email Summary */}
      <EmailSummaryPopup
        isOpen={showEmailPopup}
        onClose={() => setShowEmailPopup(false)}
        defaultEmail={defaultEmail}
        onSend={(email, patientName) => onSendEmail(email, patientName)}
        departmentMembers={departmentMembers}
      />

      {/* 2. Popup: Treatment Incomplete Warning */}
      <GenericPopup
        isOpen={showIncompletePopup}
        title="הטיפול התרופתי עדיין לא הושלם"
        onClose={() => setShowIncompletePopup(false)}

        primaryButtonText="השלם טיפול"
        onPrimaryClick={() => setShowIncompletePopup(false)}

        secondaryButtonText="דלג"
        onSecondaryClick={() => {
          setShowIncompletePopup(false);
          if (pendingRecheckTarget) {
            onOpenSection(pendingRecheckTarget);
            
            // Extract the round number from the target key and open the calculator
            let round = 1;
            if (pendingRecheckTarget === 'RECHECK_2') round = 2;
            if (pendingRecheckTarget === 'RECHECK_3') round = 3;
            
            // Assume we add this required prop to handle opening the calculator
            if (onSkipToRecheck) {
              onSkipToRecheck(round);
            }
          }
        }}
      >
        <p>
          עפ"י פרוטוקול טיפול באסטמה נותרו עוד שלבים בטיפול התרופתי לפני ביצוע ההערכה.
          <br />
          בטוח שברצונך לדלג עליהם ולעבור להערכה חוזרת?
        </p>
      </GenericPopup>

      {/* 3. Popup: Allergy Suspicion */}
      <GenericPopup
        isOpen={showAllergyPopup}
        title="יש חשד לאלרגיה?"
        onClose={() => setShowAllergyPopup(false)}

        primaryButtonText="הבנתי"
        onPrimaryClick={() => setShowAllergyPopup(false)}
      >
        <p>
          מאחר והמצב של ההתקף החמיר יש לשקול מחדש את האבחנה המבדלת ולשקול אנפילקסיס.
        </p>
      </GenericPopup>

      {/* 4. Popup: Steroids Recommendation */}
      <GenericPopup
        isOpen={showSteroidPopup}
        title="כדאי לתת סטרואידים?"
        onClose={() => setShowSteroidPopup(false)}

        primaryButtonText="הבנתי"
        onPrimaryClick={() => setShowSteroidPopup(false)}
      >
        <p>
          יש לשקול לשקול מתן סטרואידים פומית
        </p>
      </GenericPopup>

      {/* 5. Popup: Close Treatment Confirmation */}
      <GenericPopup
        isOpen={showCloseConfirmation}
        title="רגע לפני סגירה"
        onClose={() => setShowCloseConfirmation(false)}

        primaryButtonText={isEmailSent ? "סגור וסיים" : "ייצוא סיכום"}
        onPrimaryClick={() => {
          if (isEmailSent) {
            setShowCloseConfirmation(false);
            onReset();
          } else {
            setShowCloseConfirmation(false);
            onRequestEmail();
          }
        }}

        secondaryButtonText={isEmailSent ? undefined : "סגור ללא שליחה"}
        onSecondaryClick={() => {
          setShowCloseConfirmation(false);
          if (!isEmailSent) onReset();
        }}
      >
        <p>
          {isEmailSent
            ? 'הסיכום נשלח למייל. האם לסיים ולמחוק את הנתונים?'
            : 'לאחר סגירת הטיפול הנתונים יימחקו. רוצה לשלוח סיכום למייל לפני כן?'}
        </p>
      </GenericPopup>

      {/* 6. Popup: Premature Close Warning */}
      <GenericPopup
        isOpen={showPrematureClosePopup}
        title="לסגור טיפול שלא הושלם?"
        onClose={() => setShowPrematureClosePopup(false)}

        primaryButtonText="המשך בטיפול"
        onPrimaryClick={() => setShowPrematureClosePopup(false)}

        secondaryButtonText="סגור"
        onSecondaryClick={() => {
          setShowPrematureClosePopup(false);
          onReset();
        }}
      >
        <p>
          בסגירת הטיפול הנתונים יימחקו ולא יהיה ניתן לחזור אליו שוב.
          <br />
          בטוח שברצונך לסיים את הטיפול לפני השלמתו?
        </p>
      </GenericPopup>
      {/* 7. Popup: Login Required */}
      <GenericPopup
        isOpen={showLoginRequiredPopup}
        title="נדרשת התחברות למערכת"
        onClose={() => setShowLoginRequiredPopup(false)}

        primaryButtonText="הבנתי"
        onPrimaryClick={() => setShowLoginRequiredPopup(false)}
      >
        <p>
          על מנת לשלוח סיכום במייל יש להתחבר למערכת תחילה.
        </p>
      </GenericPopup>
    </>
  );
};